/**
 * Empfehlungsbonus-Analytics (Iter88za2, 17.02.2026)
 * Sales-Director/Admin/Super-Admin sehen: monatliche Anspruch- vs. Freigabe-Zahlen,
 * Top-Empfehlungsgeber, und die letzten 15 Anspruchs-Ereignisse.
 */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Loader2, TrendingUp, Users, Clock, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";

const eur = (n) =>
  Number(n || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 });

const StatCard = ({ label, value, sub, icon: Icon, tone = "amber", testid }) => {
  const tones = {
    amber: "border-amber-500/40 bg-amber-500/5 text-amber-300",
    emerald: "border-emerald-500/40 bg-emerald-500/5 text-emerald-300",
    red: "border-red-500/40 bg-red-500/5 text-red-300",
    zinc: "border-zinc-700 bg-[#18181B] text-zinc-200",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold opacity-80">
        <Icon size={14} /> {label}
      </div>
      <div className="mt-1.5 text-2xl font-black text-white tabular-nums">{value}</div>
      {sub && <div className="text-[11.5px] text-zinc-400 mt-0.5">{sub}</div>}
    </div>
  );
};

export default function EmpfehlungsbonusAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/analytics/empfehlungsbonus?months=${months}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [months]);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const maxMonthly = useMemo(() => {
    if (!data?.by_month) return 1;
    return Math.max(1, ...data.by_month.map(m => m.claimed));
  }, [data]);

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <AppHeader />
      <main className="max-w-6xl mx-auto p-4 pb-24 space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-black text-amber-300 flex items-center gap-2">
              <Gift size={24} /> Empfehlungsbonus-Auswertung
            </h1>
            <p className="text-[12.5px] text-zinc-400 mt-0.5">
              Wieviele Boni wurden beansprucht, freigegeben und wer sind die aktivsten Empfehlungsgeber
            </p>
          </div>
          <select
            data-testid="analytics-months-select"
            value={months}
            onChange={(e) => setMonths(parseInt(e.target.value))}
            className="h-10 bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-[13px]"
          >
            <option value={3}>Letzte 3 Monate</option>
            <option value={6}>Letzte 6 Monate</option>
            <option value={12}>Letzte 12 Monate</option>
            <option value={24}>Letzte 24 Monate</option>
          </select>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-zinc-400"><Loader2 className="animate-spin" size={16} /> Lade Auswertung …</div>
        ) : !data ? (
          <div className="text-zinc-400">Keine Daten.</div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-summary">
              <StatCard
                testid="stat-claimed"
                label="Beansprucht"
                value={data.totals.claimed}
                sub={eur(data.totals.total_amount_claimed)}
                icon={TrendingUp} tone="amber"
              />
              <StatCard
                testid="stat-approved"
                label="Freigegeben"
                value={data.totals.approved}
                sub={eur(data.totals.total_amount_approved)}
                icon={CheckCircle2} tone="emerald"
              />
              <StatCard
                testid="stat-pending"
                label="Offen"
                value={data.totals.pending}
                sub="Warten auf Vertriebs-Freigabe"
                icon={Clock} tone="zinc"
              />
              <StatCard
                testid="stat-rejected"
                label="Abgelehnt"
                value={data.totals.rejected}
                sub="—"
                icon={XCircle} tone="red"
              />
            </div>

            {/* Monthly bar chart (simple CSS bars) */}
            <section className="rounded-2xl border border-zinc-700 bg-[#18181B] p-4" data-testid="analytics-monthly-chart">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={16} className="text-amber-400" />
                <h2 className="font-bold text-[14px] text-zinc-100">Verlauf nach Monat</h2>
              </div>
              <div className="overflow-x-auto pb-1">
                <div
                  className="flex gap-2 items-end h-40 border-b border-zinc-800 pb-2"
                  style={{ minWidth: `${data.by_month.length * 42}px` }}
                >
                  {data.by_month.map((m, idx) => {
                    const h = (m.claimed / maxMonthly) * 100;
                    const approvedRatio = m.claimed ? (m.approved / m.claimed) * h : 0;
                    return (
                      <div key={idx} className="flex-1 min-w-[32px] flex flex-col items-center justify-end group relative">
                        <div className="w-full bg-amber-500/30 rounded-t relative" style={{ height: `${h}%` }} title={`${m.claimed} beansprucht`}>
                          <div className="absolute bottom-0 left-0 right-0 bg-emerald-500 rounded-t" style={{ height: `${approvedRatio}%` }} />
                        </div>
                        <div className="text-[9px] text-zinc-500 mt-1 truncate w-full text-center">{m.month.slice(2)}</div>
                        {m.claimed > 0 && (
                          <div className="absolute -top-8 opacity-0 group-hover:opacity-100 transition bg-[#09090B] border border-zinc-700 rounded px-2 py-1 text-[10px] whitespace-nowrap z-10 shadow-lg">
                            {m.claimed} bean. · {m.approved} freigg. · {eur(m.amount_approved)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-3 mt-2 text-[10.5px] text-zinc-400">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-500/50" /> Beansprucht</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-500" /> Davon freigegeben</span>
              </div>
            </section>

            {/* Top Referrers */}
            <section className="rounded-2xl border border-zinc-700 bg-[#18181B] p-4" data-testid="analytics-top-referrers">
              <div className="flex items-center gap-2 mb-3">
                <Users size={16} className="text-amber-400" />
                <h2 className="font-bold text-[14px] text-zinc-100">Top-Empfehlungsgeber</h2>
              </div>
              {data.top_referrers.length === 0 ? (
                <p className="text-[13px] text-zinc-500 italic">Noch keine Empfehlungen erfasst.</p>
              ) : (
                <div className="space-y-2">
                  {data.top_referrers.map((r, i) => (
                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-[#0F1418] border border-zinc-800" data-testid={`referrer-row-${i}`}>
                      <div className="w-7 h-7 rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center font-black text-[12px]">#{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold truncate text-zinc-100">{r.name}</div>
                        <div className="text-[11px] text-zinc-400">
                          {r.count} Empfehlung{r.count !== 1 ? "en" : ""} · {r.approved_count} freigegeben
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[13px] font-black text-amber-300 tabular-nums">{eur(r.total_amount)}</div>
                        <div className="text-[10px] text-zinc-500">Gesamt-Anspruch</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Recent Claims */}
            <section className="rounded-2xl border border-zinc-700 bg-[#18181B] p-4" data-testid="analytics-recent">
              <div className="flex items-center gap-2 mb-3">
                <Clock size={16} className="text-amber-400" />
                <h2 className="font-bold text-[14px] text-zinc-100">Letzte Anspruchs-Ereignisse</h2>
              </div>
              {data.recent.length === 0 ? (
                <p className="text-[13px] text-zinc-500 italic">Noch keine Ansprüche.</p>
              ) : (
                <div className="space-y-1.5">
                  {data.recent.map((r) => (
                    <Link
                      to={`/projects/${r.project_id}`}
                      key={r.offer_id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#0F1418] border border-transparent hover:border-zinc-800 transition group"
                      data-testid={`recent-row-${r.offer_id}`}
                    >
                      <div className={`w-1.5 h-9 rounded-full ${r.status === "approved" ? "bg-emerald-500" : r.status === "rejected" ? "bg-red-500" : "bg-amber-500"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-zinc-100 truncate">
                          {r.customer_name} · <span className="text-zinc-500">{r.offer_number}</span>
                        </div>
                        <div className="text-[11px] text-zinc-400 truncate">
                          {r.referrer_name ? `Empfohlen durch ${r.referrer_name}` : "kein Empfehlungsgeber-Name"}
                          {" · "}{new Date(r.claimed_at).toLocaleDateString("de-DE")}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[13px] font-black text-zinc-100 tabular-nums">{eur(r.amount_eur)}</div>
                        <div className={`text-[10px] font-semibold uppercase tracking-wider ${r.status === "approved" ? "text-emerald-400" : r.status === "rejected" ? "text-red-400" : "text-amber-400"}`}>
                          {r.status === "approved" ? "freigegeben" : r.status === "rejected" ? "abgelehnt" : "offen"}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
